import { Employee } from "../types/employee.types";
import { Shop, CoverageRequirement } from "../types/shop.types";
import { Schedule, WorkingHours } from "../types/schedule.types";
import { DayOfWeek } from "../types/common.types";
import {
  TodayOverview,
  DayOverview,
  WeekOverview,
  CoverageStatus,
  CoverageGap,
  WeekSummary,
  DayShiftCount,
  EmployeeStatistics,
  ShopEmployeeCount,
  AvailabilityBreakdown,
  ScheduleHealth,
  UnfilledShift,
  OverScheduledWarning,
  QuickAction,
  ShopPerformance,
  TodayStaffMember,
  TodayShift,
} from "../types/dashboard.types";
import {
  getWeekStart,
  getWeekDates,
  dayLabels,
  formatDate,
} from "./scheduleHelpers";
import { startTimeToMinutes } from "./timeHelpers";
import {
  calculateAllCoverageGaps,
  generateSlotMinutes,
  countEmployeesCoveringSlot,
} from "./coverageHelpers";

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the day of week for a date
 */
const getDayOfWeek = (date: Date): DayOfWeek => {
  const days: DayOfWeek[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return days[date.getDay()];
};

/**
 * Format a date for display (e.g., "Mon 6")
 */
const formatDisplayDate = (dateString: string): string => {
  const date = new Date(dateString + "T00:00:00");
  const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${dayName} ${date.getDate()}`;
};

/**
 * Get shop hours for a specific date
 */
const getShopHoursForDate = (shop: Shop, dateString: string) => {
  // Check for date-specific hours first
  if (shop.hoursConfig?.[dateString]) {
    return shop.hoursConfig[dateString];
  }

  // Fall back to default hours for the day of week
  const date = new Date(dateString + "T00:00:00");
  const dayOfWeek = getDayOfWeek(date);
  return shop.defaultHours[dayOfWeek];
};

/**
 * Get coverage requirements for a shop on a specific date
 */
const getShopCoverageForDate = (
  shop: Shop,
  dateString: string
): CoverageRequirement[] => {
  // Check for date-specific coverage first
  if (shop.dateCoverage?.[dateString]) {
    return shop.dateCoverage[dateString];
  }

  // Fall back to default coverage for the day of week
  const date = new Date(dateString + "T00:00:00");
  const dayOfWeek = getDayOfWeek(date);
  return shop.defaultCoverage?.[dayOfWeek] || [];
};

/**
 * Collect all working hours from a schedule, optionally filtering by employee availability
 */
const collectWorkingHours = (
  schedule: Schedule | undefined,
  employees?: Employee[],
  date?: string
): WorkingHours[] => {
  const workingHours: WorkingHours[] = [];

  if (!schedule?.employeeSchedules) return workingHours;

  schedule.employeeSchedules.forEach((es) => {
    if (!es.workingHours || es.workingHours.length === 0) return;

    // If employees and date provided, check availability
    if (employees && date) {
      const employee = employees.find((e) => e.id === es.employeeId);
      if (!employee || employee.status !== "active") return;

      const availability = employee.availability?.[date];
      // Skip if availability is explicitly set to 0
      if (availability && availability.hoursAvailable <= 0) return;
    }

    workingHours.push(...es.workingHours);
  });

  return workingHours;
};

/**
 * Count employees covering a specific time slot in minutes (considering availability)
 * Uses the centralized utility for accurate overnight handling
 */
const countEmployeesAtSlotMinutes = (
  slotMinutes: number,
  workingHours: WorkingHours[]
): number => {
  return countEmployeesCoveringSlot(slotMinutes, workingHours);
};

/**
 * Calculate total hours from schedules
 */
const calculateTotalHours = (schedules: Schedule[]): number => {
  let total = 0;
  schedules.forEach((schedule) => {
    schedule.employeeSchedules?.forEach((es) => {
      es.workingHours?.forEach((wh) => {
        total += wh.duration;
      });
    });
  });
  return Math.round(total * 10) / 10;
};

/**
 * Count unique employees from schedules who have actual working hours
 */
const countUniqueEmployees = (schedules: Schedule[]): number => {
  const uniqueIds = new Set<string>();
  schedules.forEach((schedule) => {
    schedule.employeeSchedules?.forEach((es) => {
      if (es.workingHours && es.workingHours.length > 0) {
        uniqueIds.add(es.employeeId);
      }
    });
  });
  return uniqueIds.size;
};

/**
 * Count employees who are working for a specific date
 * Logic:
 * 1. Check if the shop is not closed on that day
 * 2. Get the employee's availability for that day
 * 3. If availability is 0, don't count the employee
 * 4. If availability > 0 (or not set), check if scheduled hours != 0, then count as working
 */
const countAvailableWorkingEmployees = (
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  date: string
): number => {
  const workingEmployeeIds = new Set<string>();

  schedules.forEach((schedule) => {
    // 1. Check if the shop is not closed on that day
    const shop = shops.find((s) => s.id === schedule.shopId);
    if (!shop) return;

    const shopHours = getShopHoursForDate(shop, date);
    if (!shopHours?.isOpen) return; // Shop is closed, skip

    // Loop through employee schedules for this shop
    schedule.employeeSchedules?.forEach((es) => {
      // 3. Check if scheduled hours != 0
      if (!es.workingHours || es.workingHours.length === 0) return;

      // Calculate total scheduled hours for this employee
      const totalScheduledHours = es.workingHours.reduce(
        (sum, wh) => sum + wh.duration,
        0
      );
      if (totalScheduledHours === 0) return;

      // Find the employee
      const employee = employees.find((e) => e.id === es.employeeId);
      if (!employee || employee.status !== "active") return;

      // 2. Get the employee's availability for that day
      const availability = employee.availability?.[date];

      // 4. If availability is 0, don't count the employee
      if (availability && availability.hoursAvailable === 0) return;

      // If availability > 0 or not set, and has scheduled hours, count as working
      workingEmployeeIds.add(es.employeeId);
    });
  });

  return workingEmployeeIds.size;
};

// ============================================
// TODAY'S OVERVIEW CALCULATIONS
// ============================================

export function calculateTodayOverview(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  targetDate?: string
): TodayOverview {
  const date = targetDate || formatDate(new Date());
  const dateSchedules = schedules.filter((s) => s.date === date);
  const activeEmployees = employees.filter((e) => e.status === "active");

  // Count employees who are working on this date
  const employeesWorking = countAvailableWorkingEmployees(
    employees,
    shops,
    dateSchedules,
    date
  );

  // Calculate coverage status using time-based approach (considering availability)
  const coverageStatus = calculateCoverageStatus(
    shops,
    dateSchedules,
    date,
    employees
  );

  // Find coverage gaps (considering availability)
  const coverageGaps = findCoverageGaps(
    shops,
    dateSchedules,
    date,
    employees
  );

  // Calculate total scheduled hours (only from available employees)
  const totalScheduledHours = calculateTotalHoursForAvailableEmployees(
    employees,
    dateSchedules,
    date
  );

  return {
    employeesWorking,
    totalEmployees: activeEmployees.length,
    coverageStatus,
    coverageGaps,
    totalScheduledHours,
  };
}

/**
 * Calculate day overview for a specific date (with date info included)
 */
export function calculateDayOverview(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  targetDate: string
): DayOverview {
  const baseOverview = calculateTodayOverview(employees, shops, schedules, targetDate);
  const dateObj = new Date(targetDate + "T00:00:00");

  return {
    ...baseOverview,
    date: targetDate,
    displayDate: dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  };
}

/**
 * Calculate week overview (aggregated across the week)
 */
export function calculateWeekOverview(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  weekStartDate: Date
): WeekOverview {
  const weekDates = getWeekDates(weekStartDate);
  const activeEmployees = employees.filter((e) => e.status === "active");

  // Calculate data for each day
  const dailyData = weekDates.map((date) => {
    const dateStr = formatDate(date);
    const dateSchedules = schedules.filter((s) => s.date === dateStr);

    const coverageGaps = findCoverageGaps(shops, dateSchedules, dateStr, employees);
    const coverageStatus = calculateCoverageStatus(shops, dateSchedules, dateStr, employees);
    const scheduledHours = calculateTotalHoursForAvailableEmployees(employees, dateSchedules, dateStr);

    return {
      date: dateStr,
      displayDate: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      gaps: coverageGaps,
      coverageStatus,
      scheduledHours,
    };
  });

  // Aggregate coverage status across the week
  let totalFilled = 0;
  let totalRequired = 0;
  dailyData.forEach((day) => {
    totalFilled += day.coverageStatus.filled;
    totalRequired += day.coverageStatus.required;
  });

  const percentage = totalRequired > 0 ? Math.round((totalFilled / totalRequired) * 100) : 100;
  const aggregateCoverageStatus: CoverageStatus = {
    percentage,
    status: percentage >= 90 ? "good" : percentage >= 70 ? "warning" : "critical",
    filled: totalFilled,
    required: totalRequired,
  };

  // Count unique employees scheduled this week
  const scheduledEmployeeIds = new Set<string>();
  schedules.forEach((schedule) => {
    schedule.employeeSchedules?.forEach((es) => {
      const employee = employees.find((e) => e.id === es.employeeId);
      if (employee && employee.status === "active" && es.workingHours && es.workingHours.length > 0) {
        scheduledEmployeeIds.add(es.employeeId);
      }
    });
  });

  // Total scheduled hours for the week
  const totalScheduledHours = dailyData.reduce((sum, day) => sum + day.scheduledHours, 0);

  // Total coverage gaps
  const totalCoverageGaps = dailyData.reduce((sum, day) => sum + day.gaps.length, 0);

  // Format display range
  const weekStart = formatDate(weekDates[0]);
  const weekEnd = formatDate(weekDates[weekDates.length - 1]);
  const displayRange = `${weekDates[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekDates[weekDates.length - 1].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return {
    weekStart,
    weekEnd,
    displayRange,
    employeesScheduled: scheduledEmployeeIds.size,
    totalEmployees: activeEmployees.length,
    coverageStatus: aggregateCoverageStatus,
    coverageGapsByDay: dailyData.filter((d) => d.gaps.length > 0).map((d) => ({
      date: d.date,
      displayDate: d.displayDate,
      gaps: d.gaps,
    })),
    totalCoverageGaps,
    totalScheduledHours: Math.round(totalScheduledHours * 10) / 10,
  };
}

/**
 * Calculate total hours only from employees who are available
 */
const calculateTotalHoursForAvailableEmployees = (
  employees: Employee[],
  schedules: Schedule[],
  date: string
): number => {
  let total = 0;
  schedules.forEach((schedule) => {
    schedule.employeeSchedules?.forEach((es) => {
      const employee = employees.find((e) => e.id === es.employeeId);
      if (!employee || employee.status !== "active") return;

      const availability = employee.availability?.[date];
      // Include hours if available > 0 OR if no availability set
      if (!availability || availability.hoursAvailable > 0) {
        es.workingHours?.forEach((wh) => {
          total += wh.duration;
        });
      }
    });
  });
  return Math.round(total * 10) / 10;
};

function calculateCoverageStatus(
  shops: Shop[],
  schedules: Schedule[],
  date: string,
  employees?: Employee[]
): CoverageStatus {
  let totalRequiredSlots = 0;
  let totalFilledSlots = 0;

  shops.forEach((shop) => {
    const hours = getShopHoursForDate(shop, date);
    if (!hours?.isOpen) return;

    const schedule = schedules.find((s) => s.shopId === shop.id);
    const coverageReqs = getShopCoverageForDate(shop, date);

    // If no coverage requirements defined, treat as no coverage needed for this day
    if (coverageReqs.length === 0) return;

    // Collect working hours with availability filtering
    const workingHours = collectWorkingHours(schedule, employees, date);

    // Use defined coverage requirements with minute-based slots for accurate overnight handling
    coverageReqs.forEach((req) => {
      const slotMinutes = generateSlotMinutes(req.startTime, req.endTime);
      slotMinutes.forEach((slotMin) => {
        const employeesAtSlot = countEmployeesAtSlotMinutes(slotMin, workingHours);
        totalRequiredSlots += req.minCoverage;
        totalFilledSlots += Math.min(employeesAtSlot, req.minCoverage);
      });
    });
  });

  const percentage =
    totalRequiredSlots > 0
      ? Math.round((totalFilledSlots / totalRequiredSlots) * 100)
      : 100;

  return {
    percentage,
    status:
      percentage >= 100 ? "good" : percentage >= 80 ? "warning" : "critical",
    filled: totalFilledSlots,
    required: totalRequiredSlots,
  };
}

function findCoverageGaps(
  shops: Shop[],
  schedules: Schedule[],
  date: string,
  employees?: Employee[]
): CoverageGap[] {
  const gaps: CoverageGap[] = [];

  shops.forEach((shop) => {
    const hours = getShopHoursForDate(shop, date);
    if (!hours?.isOpen) return;

    const schedule = schedules.find((s) => s.shopId === shop.id);
    const coverageReqs = getShopCoverageForDate(shop, date);

    // If no coverage requirements defined, treat as no coverage needed for this day
    if (coverageReqs.length === 0) return;

    // Collect working hours with availability filtering
    const workingHours = collectWorkingHours(schedule, employees, date);

    // Use centralized coverage gap calculation
    const shopGaps = calculateAllCoverageGaps(coverageReqs, workingHours);

    // Convert to CoverageGap format with shop info
    shopGaps.forEach((gap) => {
      gaps.push({
        shopId: shop.id,
        shopName: shop.name,
        shopColor: shop.color,
        timeSlot: `${gap.startTime}-${gap.endTime}`,
        shortage: gap.shortage,
      });
    });
  });

  return gaps;
}

// ============================================
// WEEK SUMMARY CALCULATIONS
// ============================================

export function calculateWeekSummary(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[]
): WeekSummary {
  const today = new Date();
  const weekStart = getWeekStart(today);
  const weekDates = getWeekDates(weekStart);
  const weekDateStrings = weekDates.map(formatDate);
  const todayString = formatDate(today);

  const weekSchedules = schedules.filter((s) =>
    weekDateStrings.includes(s.date)
  );

  // Calculate total hours for the week
  const totalHours = calculateTotalHours(weekSchedules);

  // Calculate average coverage (passing employees for availability checking)
  const averageCoverage = calculateAverageCoverage(
    shops,
    weekSchedules,
    weekDateStrings,
    employees
  );

  // Calculate shifts by day
  const shiftsByDay = calculateShiftsByDay(
    weekSchedules,
    weekDates,
    todayString
  );

  // Count unique employees scheduled this week
  const employeesScheduled = countUniqueEmployees(weekSchedules);

  return {
    totalHours,
    averageCoverage,
    shiftsByDay,
    employeesScheduled,
  };
}

function calculateAverageCoverage(
  shops: Shop[],
  schedules: Schedule[],
  dates: string[],
  employees?: Employee[]
): number {
  let totalPercentage = 0;
  let daysWithRequirements = 0;

  dates.forEach((date) => {
    const status = calculateCoverageStatus(
      shops,
      schedules.filter((s) => s.date === date),
      date,
      employees
    );
    if (status.required > 0) {
      totalPercentage += status.percentage;
      daysWithRequirements++;
    }
  });

  return daysWithRequirements > 0
    ? Math.round(totalPercentage / daysWithRequirements)
    : 100;
}

function calculateShiftsByDay(
  schedules: Schedule[],
  dates: Date[],
  todayString: string
): DayShiftCount[] {
  return dates.map((date) => {
    const dateString = formatDate(date);
    const daySchedules = schedules.filter((s) => s.date === dateString);
    let shiftCount = 0;

    daySchedules.forEach((schedule) => {
      schedule.employeeSchedules?.forEach((es) => {
        shiftCount += es.workingHours?.length || 0;
      });
    });

    const dayOfWeek = getDayOfWeek(date);

    return {
      date: dateString,
      dayLabel: dayLabels[dayOfWeek],
      count: shiftCount,
      isToday: dateString === todayString,
    };
  });
}

// ============================================
// EMPLOYEE STATISTICS CALCULATIONS
// ============================================

export function calculateEmployeeStats(
  employees: Employee[],
  shops: Shop[],
  dates?: string[]
): EmployeeStatistics {
  const activeEmployees = employees.filter((e) => e.status === "active");
  const inactiveEmployees = employees.filter((e) => e.status === "inactive");

  // Use provided dates or default to current week
  const targetDates = dates || (() => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    return getWeekDates(weekStart).map(formatDate);
  })();

  return {
    totalActive: activeEmployees.length,
    totalInactive: inactiveEmployees.length,
    byShop: calculateEmployeesByShop(employees, shops),
    availabilityStatus: calculateAvailabilityStatus(activeEmployees, targetDates),
  };
}

function calculateEmployeesByShop(
  employees: Employee[],
  shops: Shop[]
): ShopEmployeeCount[] {
  return shops.map((shop) => ({
    shopId: shop.id,
    shopName: shop.name,
    shopColor: shop.color,
    count: employees.filter(
      (e) => e.status === "active" && e.assignedShopIds.includes(shop.id)
    ).length,
  }));
}

function calculateAvailabilityStatus(
  employees: Employee[],
  dates: string[]
): AvailabilityBreakdown {
  let withAvailability = 0;
  const withAvailabilityNames: string[] = [];
  let withoutAvailability = 0;
  const withoutAvailabilityNames: string[] = [];
  let partialAvailability = 0;
  const partialAvailabilityNames: string[] = [];

  employees.forEach((emp) => {
    const daysWithAvailability = dates.filter((date) => {
      const avail = emp.availability?.[date];
      // Count as "set" if availability exists, even if hours is 0 (holiday/day off)
      return avail !== undefined && avail.hoursAvailable !== undefined;
    }).length;

    if (daysWithAvailability === dates.length) {
      withAvailability++;
      withAvailabilityNames.push(emp.name);
    } else if (daysWithAvailability === 0) {
      withoutAvailability++;
      withoutAvailabilityNames.push(emp.name);
    } else {
      partialAvailability++;
      partialAvailabilityNames.push(emp.name);
    }
  });

  return {
    withAvailability,
    withAvailabilityNames,
    withoutAvailability,
    withoutAvailabilityNames,
    partialAvailability,
    partialAvailabilityNames,
  };
}

// ============================================
// SCHEDULE HEALTH CALCULATIONS
// ============================================

export function calculateScheduleHealth(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  dates?: string[]
): ScheduleHealth {
  // Use provided dates or default to current week
  const targetDates = dates || (() => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    return getWeekDates(weekStart).map(formatDate);
  })();

  const unfilledShifts = findUnfilledShifts(shops, schedules, targetDates);
  const overScheduledWarnings = findOverScheduledEmployees(
    employees,
    schedules,
    targetDates
  );

  const totalIssues = unfilledShifts.length + overScheduledWarnings.length;

  return {
    overallStatus:
      totalIssues === 0 ? "healthy" : totalIssues <= 3 ? "warning" : "critical",
    unfilledShifts,
    overScheduledWarnings,
    totalIssues,
  };
}

function findUnfilledShifts(
  shops: Shop[],
  schedules: Schedule[],
  dates: string[]
): UnfilledShift[] {
  const unfilled: UnfilledShift[] = [];

  dates.forEach((date) => {
    shops.forEach((shop) => {
      const hours = getShopHoursForDate(shop, date);
      if (!hours?.isOpen) return;

      const schedule = schedules.find(
        (s) => s.shopId === shop.id && s.date === date
      );
      const coverageReqs = getShopCoverageForDate(shop, date);

      // If no coverage requirements defined, treat as no coverage needed for this day
      if (coverageReqs.length === 0) return;

      // Collect working hours for this schedule
      const workingHours = collectWorkingHours(schedule);

      // Use centralized coverage gap calculation for consistency
      const gaps = calculateAllCoverageGaps(coverageReqs, workingHours);

      gaps.forEach((gap) => {
        unfilled.push({
          shopId: shop.id,
          shopName: shop.name,
          shopColor: shop.color,
          date,
          displayDate: formatDisplayDate(date),
          timeSlot: `${gap.startTime}-${gap.endTime}`,
          required: gap.required,
          filled: Math.max(0, gap.required - gap.shortage),
          shortage: gap.shortage,
        });
      });
    });
  });

  return unfilled;
}

function findOverScheduledEmployees(
  employees: Employee[],
  schedules: Schedule[],
  dates: string[]
): OverScheduledWarning[] {
  const warnings: OverScheduledWarning[] = [];

  employees.forEach((emp) => {
    if (emp.status !== "active") return;

    dates.forEach((date) => {
      const availability = emp.availability?.[date];
      if (!availability) return;

      let totalScheduledHours = 0;
      schedules.forEach((schedule) => {
        if (schedule.date !== date) return;
        const empSchedule = schedule.employeeSchedules?.find(
          (es) => es.employeeId === emp.id
        );
        empSchedule?.workingHours?.forEach((wh) => {
          totalScheduledHours += wh.duration;
        });
      });

      if (totalScheduledHours > availability.hoursAvailable) {
        warnings.push({
          employeeId: emp.id,
          employeeName: emp.name,
          date,
          displayDate: formatDisplayDate(date),
          availableHours: availability.hoursAvailable,
          scheduledHours: totalScheduledHours,
          excessHours:
            Math.round(
              (totalScheduledHours - availability.hoursAvailable) * 10
            ) / 10,
        });
      }
    });
  });

  return warnings;
}

// ============================================
// QUICK ACTIONS CALCULATIONS
// ============================================

export function calculateQuickActions(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[]
): QuickAction[] {
  const actions: QuickAction[] = [];
  const today = new Date();
  const weekStart = getWeekStart(today);
  const weekDates = getWeekDates(weekStart).map(formatDate);

  // Find employees missing availability
  const activeEmployees = employees.filter((e) => e.status === "active");
  const missingAvailability = activeEmployees.filter((emp) => {
    const daysWithAvailability = weekDates.filter((date) => {
      const avail = emp.availability?.[date];
      return avail && avail.hoursAvailable > 0;
    }).length;
    return daysWithAvailability === 0;
  });

  if (missingAvailability.length > 0) {
    actions.push({
      id: "missing-availability",
      type: "warning",
      title: "Missing Availability",
      description: `${missingAvailability.length} employee(s) haven't set availability for this week`,
      count: missingAvailability.length,
      actionLabel: "View Employees",
      actionPath: "/employees/availability",
      items: missingAvailability.slice(0, 5).map((emp) => ({
        id: emp.id,
        label: emp.name,
      })),
    });
  }

  // Find unfilled shifts
  const unfilledShifts = findUnfilledShifts(shops, schedules, weekDates);
  if (unfilledShifts.length > 0) {
    actions.push({
      id: "unfilled-shifts",
      type: "error",
      title: "Unfilled Shifts",
      description: `${unfilledShifts.length} shift(s) need more coverage`,
      count: unfilledShifts.length,
      actionLabel: "View Schedules",
      actionPath: "/schedules/employees",
      items: unfilledShifts.slice(0, 5).map((shift) => ({
        id: `${shift.shopId}-${shift.date}`,
        label: `${shift.shopName}: ${shift.timeSlot} (${shift.displayDate})`,
      })),
    });
  }

  // Shops without coverage configuration
  const shopsWithoutCoverage = shops.filter((shop) => {
    if (!shop.defaultCoverage) return true;
    const hasCoverage = Object.values(shop.defaultCoverage).some(
      (coverage) => coverage && coverage.length > 0
    );
    return !hasCoverage;
  });

  if (shopsWithoutCoverage.length > 0) {
    actions.push({
      id: "no-coverage-config",
      type: "info",
      title: "Coverage Not Configured",
      description: `${shopsWithoutCoverage.length} shop(s) don't have coverage requirements set`,
      count: shopsWithoutCoverage.length,
      actionLabel: "Configure Coverage",
      actionPath: "/shops/coverage",
      items: shopsWithoutCoverage.map((shop) => ({
        id: shop.id,
        label: shop.name,
      })),
    });
  }

  return actions;
}

// ============================================
// SHOP PERFORMANCE CALCULATIONS
// ============================================

export function calculateShopPerformance(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  dates?: string[]
): ShopPerformance[] {
  // Use provided dates or default to today
  const targetDates = dates || [formatDate(new Date())];
  const isMultipleDays = targetDates.length > 1;

  return shops.map((shop) => {
    const shopSchedules = schedules.filter(
      (s) => s.shopId === shop.id && targetDates.includes(s.date)
    );

    // Track unique staff and total hours across all dates
    const uniqueStaffIds = new Set<string>();
    let totalHours = 0;
    let daysOpen = 0;
    let coverageMet = true;

    // For single day, get the hours info
    let firstDayHours = getShopHoursForDate(shop, targetDates[0]);

    targetDates.forEach((date) => {
      const daySchedule = shopSchedules.find((s) => s.date === date);
      const hours = getShopHoursForDate(shop, date);

      if (hours?.isOpen) {
        daysOpen++;
      }

      // Count staff and hours for this day
      daySchedule?.employeeSchedules?.forEach((es) => {
        if (!es.workingHours || es.workingHours.length === 0) return;

        // Check if employee is available
        const employee = employees.find((e) => e.id === es.employeeId);
        if (!employee || employee.status !== "active") return;

        const availability = employee.availability?.[date];
        // Skip if availability is explicitly set to 0
        if (availability && availability.hoursAvailable <= 0) return;

        uniqueStaffIds.add(es.employeeId);
        es.workingHours.forEach((wh) => {
          totalHours += wh.duration;
        });
      });

      // Check coverage for this day
      if (hours?.isOpen) {
        const coverageReqs = getShopCoverageForDate(shop, date);

        if (coverageReqs.length > 0) {
          // Collect working hours with availability filtering
          const workingHours = collectWorkingHours(daySchedule, employees, date);

          coverageReqs.forEach((req) => {
            const slotMinutes = generateSlotMinutes(req.startTime, req.endTime);
            slotMinutes.forEach((slotMin) => {
              if (countEmployeesAtSlotMinutes(slotMin, workingHours) < req.minCoverage) {
                coverageMet = false;
              }
            });
          });
        }
      }
    });

    // Count assigned employees
    const assignedEmployees = employees.filter(
      (e) => e.status === "active" && e.assignedShopIds.includes(shop.id)
    ).length;

    return {
      shopId: shop.id,
      shopName: shop.name,
      shopColor: shop.color,
      isOpen: isMultipleDays ? daysOpen > 0 : (firstDayHours?.isOpen || false),
      openTime: isMultipleDays ? undefined : firstDayHours?.openTime,
      closeTime: isMultipleDays ? undefined : firstDayHours?.closeTime,
      staffCount: uniqueStaffIds.size,
      totalHours: Math.round(totalHours * 10) / 10,
      coverageMet,
      assignedEmployees,
      daysOpen: isMultipleDays ? daysOpen : undefined,
      totalDays: isMultipleDays ? targetDates.length : undefined,
    };
  });
}

// ============================================
// TODAY'S STAFF CALCULATIONS
// ============================================

export function calculateTodayStaff(
  employees: Employee[],
  shops: Shop[],
  schedules: Schedule[],
  dates?: string[]
): TodayStaffMember[] {
  // Use provided dates or default to today
  const targetDates = dates || [formatDate(new Date())];
  const targetSchedules = schedules.filter((s) => targetDates.includes(s.date));
  const staffMap = new Map<string, TodayStaffMember>();

  targetSchedules.forEach((schedule) => {
    const shop = shops.find((s) => s.id === schedule.shopId);
    if (!shop) return;

    schedule.employeeSchedules?.forEach((es) => {
      if (!es.workingHours || es.workingHours.length === 0) return;

      const employee = employees.find((e) => e.id === es.employeeId);
      if (!employee || employee.status !== "active") return;

      // Check if employee is available on this date
      const availability = employee.availability?.[schedule.date];
      if (availability && availability.hoursAvailable <= 0) return;

      // Get or create staff member entry
      let staffMember = staffMap.get(es.employeeId);
      if (!staffMember) {
        staffMember = {
          employeeId: es.employeeId,
          employeeName: employee.name,
          shifts: [],
          totalHours: 0,
        };
        staffMap.set(es.employeeId, staffMember);
      }

      // Add shifts for this shop
      es.workingHours.forEach((wh) => {
        const startMinutes = startTimeToMinutes(wh.startTime);
        const endMinutes = startMinutes + wh.duration * 60;
        const endHours = Math.floor(endMinutes / 60) % 24;
        const endMins = endMinutes % 60;
        const endTime = `${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`;

        const shift: TodayShift = {
          shopId: shop.id,
          shopName: shop.name,
          shopColor: shop.color,
          startTime: wh.startTime,
          endTime,
          duration: wh.duration,
        };

        staffMember!.shifts.push(shift);
        staffMember!.totalHours += wh.duration;
      });
    });
  });

  // Convert map to array and sort by earliest start time
  const staffList = Array.from(staffMap.values());

  // Sort shifts within each staff member by start time
  staffList.forEach((staff) => {
    staff.shifts.sort((a, b) => {
      const aMinutes = startTimeToMinutes(a.startTime);
      const bMinutes = startTimeToMinutes(b.startTime);
      return aMinutes - bMinutes;
    });
    staff.totalHours = Math.round(staff.totalHours * 10) / 10;
  });

  // Sort staff by total hours (descending) then by name
  staffList.sort((a, b) => {
    if (b.totalHours !== a.totalHours) {
      return b.totalHours - a.totalHours;
    }
    return a.employeeName.localeCompare(b.employeeName);
  });

  return staffList;
}
